import React from 'react';

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const hasAmountValue = (value) => value !== null && value !== undefined && value !== '';

const formatAmount = (value) => {
  if (!hasAmountValue(value)) return '';
  const n = Number(value);
  const raw = Number.isFinite(n) ? String(Math.trunc(n)) : String(value).split('.')[0];
  return raw.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const parseAmount = (value) => {
  const digits = String(value || '').replace(/,/g, '').split('.')[0].replace(/[^\d]/g, '');
  return digits === '' ? null : Number(digits);
};

export function defaultNightlyRates(count = 2) {
  return Array.from({ length: count }, (_, index) => ({ night: index + 1, amount: null }));
}

export function normalizeNightlyRates(rates, { minRows = 0, keepEmpty = true } = {}) {
  const source = Array.isArray(rates) ? rates : [];
  let rows = source.map((row, index) => ({
    night: index + 1,
    amount: hasAmountValue(row?.amount) ? num(row.amount) : null,
  }));
  if (!keepEmpty) rows = rows.filter((row) => hasAmountValue(row.amount));
  while (rows.length < minRows) rows.push({ night: rows.length + 1, amount: null });
  return rows.map((row, index) => ({ ...row, night: index + 1 }));
}

export function hasNightlyAmount(rates) {
  return normalizeNightlyRates(rates, { minRows: 0 }).some((row) => hasAmountValue(row.amount));
}

export function hotelTotal(rates) {
  return normalizeNightlyRates(rates, { minRows: 0 }).reduce((sum, row) => sum + num(row.amount), 0);
}

function hasRowValue(row = {}) {
  return Object.values(row).some((value) => {
    if (value === null || value === undefined || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return hasRowValue(value);
    return true;
  });
}

function hasHotelInput(row = {}) {
  const { notes, nightly_rates, total_cost, ...rest } = row;
  return hasRowValue(rest) || hasNightlyAmount(nightly_rates) || hasAmountValue(total_cost);
}

export function cleanHotelRows(rows = []) {
  return rows
    .filter(hasHotelInput)
    .map(({ notes, ...row }) => {
      const nightlyRates = normalizeNightlyRates(row.nightly_rates, { minRows: 0, keepEmpty: false });
      return {
        ...row,
        nightly_rates: nightlyRates,
        total_cost: nightlyRates.length ? hotelTotal(nightlyRates) : row.total_cost,
      };
    });
}

export default function HotelNightlyRatesEditor({ rates, onChange, minRows = 2, compact = false }) {
  const rows = normalizeNightlyRates(rates, { minRows });
  const total = hotelTotal(rows);

  const emit = (nextRows) => onChange(normalizeNightlyRates(nextRows, { minRows: 0 }));
  const updateAmount = (index, amount) => {
    emit(rows.map((row, rowIndex) => (rowIndex === index ? { ...row, amount } : row)));
  };
  const addRow = () => emit([...rows, { night: rows.length + 1, amount: null }]);
  const removeRow = (index) => {
    if (index < minRows) return;
    emit(rows.filter((_, rowIndex) => rowIndex !== index));
  };

  return (
    <div style={editorStyle(compact)}>
      <div style={headerStyle}>
        <span style={titleStyle}>일별요금</span>
        <strong style={totalStyle}>총액 자동 {total.toLocaleString('ko-KR')}원</strong>
      </div>
      <div style={rowWrapStyle}>
        {rows.map((row, index) => (
          <label key={index} style={rateItemStyle}>
            <span style={rateLabelStyle}>{index + 1}박</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9,]*"
              value={formatAmount(row.amount)}
              onFocus={(event) => event.target.select()}
              onChange={(event) => updateAmount(index, parseAmount(event.target.value))}
              style={amountInputStyle}
            />
            {index >= minRows && (
              <button type="button" onClick={() => removeRow(index)} style={removeBtnStyle}>삭제</button>
            )}
          </label>
        ))}
        <button type="button" onClick={addRow} style={addBtnStyle}>+ 박 추가</button>
      </div>
    </div>
  );
}

const editorStyle = (compact) => ({
  display: 'grid',
  gap: compact ? 5 : 8,
  padding: compact ? 6 : 10,
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'var(--bg)',
  minWidth: 0,
});
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' };
const titleStyle = { fontSize: 11, color: 'var(--text-muted)', fontWeight: 700 };
const totalStyle = { fontSize: 12, color: 'var(--text)', whiteSpace: 'nowrap' };
const rowWrapStyle = { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', minWidth: 0 };
const rateItemStyle = { display: 'grid', gridTemplateColumns: 'auto minmax(82px, 110px) auto', gap: 4, alignItems: 'center', minWidth: 0 };
const rateLabelStyle = { fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' };
const amountInputStyle = {
  width: '100%',
  height: 30,
  padding: '5px 8px',
  border: '1px solid var(--border)',
  borderRadius: 4,
  background: 'var(--bg-card)',
  color: 'var(--text)',
  boxSizing: 'border-box',
  textAlign: 'right',
  fontSize: 12,
};
const addBtnStyle = { height: 30, padding: '4px 9px', border: '1px solid var(--border)', borderRadius: 4, background: 'transparent', color: 'var(--text)', cursor: 'pointer', fontSize: 11, fontWeight: 700 };
const removeBtnStyle = { height: 24, padding: '2px 5px', border: '1px solid #dc2626', borderRadius: 4, background: 'transparent', color: '#dc2626', cursor: 'pointer', fontSize: 10 };
